import { Rule, RuleObject } from 'antd/lib/form';
import { FormFieldConf } from 'components/common/forms/helpers';
import { capitalize, transform } from 'lodash';

export type FormFieldsOf<T> = {
  [P in keyof T]: FormFieldConf<P>
};

export type Prefix<T, Str extends string> = {
  [P in (keyof T & string) as `${Str}${Capitalize<P>}`]: T[P];
}

type Rec = {
  [K: string]: any;
}

type KeyOf<T> = keyof T;

type FormFieldRules<T> = {
  [P in keyof T]?: Rule[]
}

export default class ModelBuilder {
  static buildFormFields<T>(fields: FormFieldsOf<T>) {
    const fieldList = Object.keys(fields).map(key => {
      const field = { name: key, ...fields[key] } as FormFieldConf<T>;
      return [key, field];
    });
    const result = Object.fromEntries(fieldList) as FormFieldsOf<T>;
    return result;
  }

  static prefixFields<T>(fields: FormFieldsOf<T>, str: string) {
    const result = transform(fields, (result: Rec, value: any, key: string) => {
      const newKey = str + capitalize(key);
      value = { ...value, name: str + capitalize(value.name) };
      result[newKey] = value;
    });
    return result;
  }

  static setRulesToFields<T>(fields: FormFieldsOf<T>, fieldsRule?: FormFieldRules<T>) {
    const fieldKeys = Object.keys(fields);
    fieldKeys.forEach(key => {
      const field = fields[key] as FormFieldConf<T>;
      if (fieldsRule[key]) {
        const prevRules = field.rules || [];
        const rules = fieldsRule[key] as RuleObject[];
        const newRules = rules.map(r => {
          let message = r.required && (r.message || `${field.label} is required`);
          return { ...r, message };
        });
        field.rules = [...prevRules, ...newRules];
      }
    });
    const result = fields as FormFieldsOf<T>;
    return result;
  }

  static setRequiredRule<T>(fields: FormFieldsOf<T>, fieldNames: KeyOf<T>[]) {
    const fieldNamesStr = fieldNames as string[];
    fieldNamesStr.forEach(name => {
      const field = fields[name] as FormFieldConf<T>;
      field.rules = field.rules || [];
      field.rules.push({ required: true, message: `${field.label} is required` });
    });
    const result = fields as FormFieldsOf<T>;
    return result;
  }
  
  static buildMapModel = <TModel,TPayload>(mapFunc: (payload: TPayload) => TModel) => {
    return ((input: TPayload[] | TPayload) => {
      const output = Array.isArray(input) ? input.map<TModel>(mapFunc) : mapFunc(input);
      return output;
    }) as {
      (input: TPayload): TModel;
      (input: TPayload[]): TModel[];
    };
  } 
}
